import React, { FC, MouseEvent, useEffect, useState, useContext, useRef } from "react";
import MaterialTable, { Column, MTableToolbar } from "material-table";
import { makeStyles, Theme } from "@material-ui/core/styles";
import { ChevronRight, KeyboardArrowDown, Search } from "@material-ui/icons";
import { clone, isEmpty, keyBy } from "lodash";
import TableIcons from "../Common/TableIcons";
import { FiltersContext, DataContext, LoadingContext, VisibilityContext, TherapeuticAreaContext } from "../../context";
import { UserContext } from "@ipd/js-common";
import { STATE, TABLE, TEXTS, PERMISSIONS } from "../../constants";
import { options, localization, cellWidths, headerStyle, cellStyle } from "./config";
import { enums, interfaces, types } from "../../typescript";
import { filter, get, sort, validate } from "../../functions";
import DetailPanel from "../DetailPanel";
import Popup from "../DrugPopup/Popup";
import StyledTooltip from "../Common/StyledTooltip";
import ToggleDetailPanels from "../Buttons/ToggleDetailPanels";
import LoadingBar from "../Common/LoadingBar";
import DrugNameTD from "./DrugNameTD";
import BrandCompanyTD from "./BrandCompanyTD";
import RouteTD from "./RouteTD";
import MechOfActionTD from "./MechOfActionTD";
import IndicationTD from "./IndicationTD";
import StageTD from "./StageTD";
import SubTD from "./SubTD";
import ViewTD from "./ViewTD";
import OverflowWrapper from "./OverflowWrapper";
import ColumnHeader from "./ColumnHeader";
import InfoIcon from "../Common/InfoIcon";
import ExportCSV from "../Buttons/ExportCSV";
import TooltipHelpIcon from "../Common/TooltipHelpIcon";
import PDF from "../Buttons/PDF";
import config from "../../config";
import { table } from "../../typescript/types";
import { usePermissions } from "../../hooks";

const useStyles = makeStyles((theme: Theme) => ({
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-evenly",

    "& h6": {
      color: theme.palette.secondary.dark,
      fontWeight: 900,
    },

    "& div.MuiToolbar-root": {
      flexGrow: 0.9,
      marginRight: "0.4rem",
    },
  },
  toolbarButtons: {
    width: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-evenly",
    marginRight: "0.4rem",

    "& button": {
      marginRight: "0.5rem",
    },
  },
  magGlass: {
    fontSize: "1.3rem",
    color: theme.palette.secondary.main,
  },
}));

const Table: FC = () => {
  const classes = useStyles();
  const tableRef = useRef<any>();

  const { hasClaim, token } = useContext(UserContext);
  const exportClaims = Object.values(PERMISSIONS.EXPORT);
  const hasExportPermission = exportClaims.some((claim) => hasClaim(claim, "LciClinicalPipeline"));

  const [filtersContext, setFiltersContext] = useContext(FiltersContext);
  const [dataContext, setDataContext] = useContext(DataContext);
  const [therapeuticAreaContext] = useContext(TherapeuticAreaContext);
  const [loadingContext, setLoadingContext] = useContext(LoadingContext);
  const [visibilityContext, setVisibilityContext] = useContext(VisibilityContext);
  const [tableData, setTableData] = useState<Array<interfaces.context.Data>>([]);
  const [isPopupOpen, setIsOpen] = useState(false);
  const [popupData, setPopupData] = useState({});
  const [anchorEl, setAnchorEl] = useState<(EventTarget & HTMLElement) | null>(null);
  const [areAllPanelsExpanded, setAreAllPanelsExpanded] = useState(false);
  const [rowToUnmount, setRowToUnmount] = useState<number | null>(null);
  const [columnsSortOrder, setColumnsSortOrder] = useState(STATE.COLUMNS_SORT_ORDERS);
  const [loading, setLoading] = useState<boolean>(false);
  const [filteredIDList, setFilteredIDList] = useState<string[]>([]);
  const [quickFilterText, setQuickFilterText] = useState<string>();

  const hasPharma = usePermissions(PERMISSIONS.PHARMA);
  const hasBiologic = usePermissions(PERMISSIONS.BIOLOGIC);

  // whether to hide links for biologics/pharma drugs if the user lacks permission
  const noLink = (isBiologic) => !(isBiologic ? hasBiologic : hasPharma);

  const handleDetailPanelRender = ({
    tags,
    complexForms,
    likelyComplexForms,
    notesAndCommentary,
    hasDrugPipelineReport,
    tableData,
  }) => {
    if (!hasDrugPipelineReport && tags.length <= 0) return null;

    return (
      <DetailPanel
        tags={tags}
        complexForms={complexForms}
        likelyComplexForms={likelyComplexForms}
        notesAndCommentary={notesAndCommentary}
        rowId={tableData.id}
        handleUnmount={handleDetailPanelUnmount}
      />
    );
  };

  const handleManualDetailPanelRender = (rowId: number) =>
    tableRef.current.onToggleDetailPanel([rowId], handleDetailPanelRender);

  const handleDetailPanelUnmount = (rowId: number) => setRowToUnmount(rowId);

  const toggleAllDetailPanels = () => {
    if (tableRef.current) {
      const filteredTableRows = tableRef.current.state.data.length;

      for (let index = 0; index < filteredTableRows; index++) {
        if (areAllPanelsExpanded && tableRef.current.state.data[index].tableData.showDetailPanel) {
          tableRef.current.onToggleDetailPanel([index], handleDetailPanelRender);
        } else if (!areAllPanelsExpanded && !tableRef.current.state.data[index].tableData.showDetailPanel) {
          tableRef.current.onToggleDetailPanel([index], handleDetailPanelRender);
        }
      }
      setAreAllPanelsExpanded(!areAllPanelsExpanded);
    }
  };

  const handleIcon = (
    hasDrugPipelineReport: boolean,
    tags: (interfaces.data.OtherTag | enums.data.FDADesignations)[],
    tableData: types.table.RowData
  ) => {
    if (!hasDrugPipelineReport && tags.length <= 0) return null;

    const isOpen = "showDetailPanel" in tableData && tableData["showDetailPanel"] !== undefined;
    const isClosed = !("showDetailPanel" in tableData) || tableData["showDetailPanel"] === undefined;

    if (isOpen) {
      return <KeyboardArrowDown fontSize="small" />;
    } else if (isClosed) {
      return <ChevronRight fontSize="small" />;
    } else {
      return null;
    }
  };

  const openPopover = (event: MouseEvent<HTMLElement>, rowData: any) => {
    setIsOpen(true);
    setPopupData(rowData);
    setAnchorEl(event.currentTarget);
  };

  const closePopover = () => setIsOpen(false);

  const handleColumnSort = (title: string) => {
    const key = get.keyByValue(TABLE.COLUMN_TITLES, title);
    const order = columnsSortOrder[key];
    const sortedRows = sort.rowsByColumn(tableData, key, order);

    setColumnsSortOrder({
      ...columnsSortOrder,
      [key]: order === "asc" ? "desc" : "asc",
    });
    setTableData(sortedRows);
    const sortedDrugIds = getSortedIds(sortedRows);
    setFilteredIDList(sortedDrugIds);
  };

  const getSortedIds = (tableData) => {
    const data = keyBy(tableRef.current.state.data, (d) => d.id);
    return tableData.filter(({ id }) => data[id]).map(({ id }) => id);
  };

  const handlePdfDownload = () => {
    if (token) {
      setLoading(true);
      const today = new Date();
      const dd = String(today.getDate()).padStart(2, "0");
      const mm = String(today.getMonth() + 1).padStart(2, "0"); //January is 0!
      const yyyy = today.getFullYear();
      const filename = `IPD Analytics Clinical Development ${mm}-${dd}-${yyyy}.pdf`;
      (async (): Promise<void> => {
        const pdfResponse = await fetch(config.PDF_URL, {
          headers: { Authorization: `Bearer ${token}` },
          method: "post",
          body: JSON.stringify({ tearsheetIds: filteredIDList, filename: filename }),
        });
        setLoading(false);
        const { download_url: downloadUrl } = await pdfResponse.json();
        window.open(downloadUrl, "_blank");
      })();
    }
  };

  const updateFilteredIDList = (data: interfaces.context.Data[]) => {
    const drugIDList = data.map(({ id }) => id);
    setFilteredIDList(drugIDList);
  };

  const handleSearchChange = (text: string, data: interfaces.context.Data[]) => {
    updateFilteredIDList(data);
    setQuickFilterText(text);
  };

  useEffect(() => {
    // if the quickfilter has text in it, focus back on that after setting filteredIDList
    if (quickFilterText) {
      (document.querySelector('[aria-label="Search"]') as HTMLElement)?.focus();
    }
  }, [filteredIDList]);

  useEffect(() => {
    if (dataContext.length > 0) {
      if (validate.noFiltersApplied(filtersContext)) {
        const deducedTableData = therapeuticAreaContext
          ? dataContext.filter((data) => data.therapeuticAreaTags?.some((tag) => tag.slug === therapeuticAreaContext))
          : dataContext;
        setTableData(deducedTableData);
      } else {
        const clonedDataContext = clone(dataContext);
        const deducedTableData = therapeuticAreaContext
          ? clonedDataContext.filter((data) =>
              data.therapeuticAreaTags?.some((tag) => tag.slug === therapeuticAreaContext)
            )
          : clonedDataContext;
        const filteredData: interfaces.context.Data[] = filter.tableData(filtersContext, deducedTableData);
        setTableData(filteredData);
        updateFilteredIDList(filteredData);
      }
    }
  }, [filtersContext, therapeuticAreaContext]);

  useEffect(() => {
    if (dataContext.length > 0) {
      const deducedTableData = therapeuticAreaContext
        ? dataContext.filter((data) => data.therapeuticAreaTags?.some((tag) => tag.slug === therapeuticAreaContext))
        : dataContext;
      setTableData(deducedTableData);
      setLoadingContext({
        ...loadingContext,
        usaClinicalPipelineTearSheets: false,
      });
      updateFilteredIDList(tableData);
    }
  }, [dataContext, therapeuticAreaContext]);

  useEffect(() => {
    const allRows = areAllPanelsExpanded ? dataContext.map((row, idx) => idx) : [];

    setVisibilityContext({
      ...visibilityContext,
      detailPanelsOpen: allRows,
      rowsExpanded: allRows,
    });
  }, [areAllPanelsExpanded]);

  useEffect(() => {
    if (typeof rowToUnmount === "number") {
      const filteredRows = visibilityContext.detailPanelsOpen.filter((rowId) => rowId !== rowToUnmount);
      setVisibilityContext({
        ...visibilityContext,
        detailPanelsOpen: filteredRows,
        rowsExpanded: filteredRows,
      });
    }
  }, [rowToUnmount]);

  const columns: Column<any>[] = [
    {
      title: <ColumnHeader title={TABLE.COLUMN_TITLES.brandName} handleSort={handleColumnSort} />,
      field: "brandName",
      type: "string",
      width: cellWidths.brandName,
      headerStyle: {
        ...headerStyle,
        paddingLeft: 0,
        textAlign: "left",
        position: "relative",
      },
      cellStyle: {
        ...cellStyle,
        position: "relative",
      },
      render: (rowData: any) => {
        const {
          drugId,
          isBiologic,
          brandName,
          genericName,
          complexForms,
          likelyComplexForms,
          notesAndCommentary,
          tableData,
          clinicalDevelopmentNotes,
        } = rowData;
        const hasOverflowNote = clinicalDevelopmentNotes.length > 0;
        return (
          <DrugNameTD
            noLink={noLink(isBiologic)}
            drugId={drugId}
            isBiologic={isBiologic}
            brandName={brandName}
            genericName={genericName}
            complexForms={complexForms}
            likelyComplexForms={likelyComplexForms}
            width={cellWidths.brandName}
            notesAndCommentary={notesAndCommentary}
            hasOverflowNote={hasOverflowNote}
            rowId={tableData.id}
            handleDetailPanelOpen={handleManualDetailPanelRender}
          />
        );
      },
      customFilterAndSearch: (filterStr, rowData) =>
        filter.tableColumn(filterStr, rowData, ["brandName", "genericName"]),
    },
    {
      title: <ColumnHeader title={TABLE.COLUMN_TITLES.brandCompanies} handleSort={handleColumnSort} />,
      field: "brandCompanies",
      type: "string",
      width: cellWidths.brandCompanies,
      cellStyle: {
        ...cellStyle,
        position: "relative",
      },
      align: "center",
      render: ({ brandCompanies, overflow, tableData, isBiologic }) => (
        <OverflowWrapper overflow={overflow["brandCompanies"]} rowId={tableData.id} displayOverflowNote>
          <BrandCompanyTD
            noLink={noLink(isBiologic)}
            brandCompanies={brandCompanies}
            width={cellWidths.brandCompanies}
            overflow={overflow["brandCompanies"]}
            rowId={tableData.id}
          />
        </OverflowWrapper>
      ),
      customFilterAndSearch: (filterStr, rowData) => filter.tableColumn(filterStr, rowData, ["brandCompanies"]),
    },
    {
      title: <ColumnHeader title={TABLE.COLUMN_TITLES.routesOfAdministration} handleSort={handleColumnSort} />,
      field: "route",
      type: "string",
      width: cellWidths.routesOfAdministration,
      cellStyle,
      align: "center",
      render: ({ routesOfAdministration }) => <RouteTD routesOfAdministration={routesOfAdministration} />,
      customFilterAndSearch: (filterStr, rowData) => filter.tableColumn(filterStr, rowData, ["routesOfAdministration"]),
    },
    {
      title: <ColumnHeader title={TABLE.COLUMN_TITLES.mechanismsOfAction} handleSort={handleColumnSort} />,
      field: "mechanismsOfAction",
      type: "string",
      width: cellWidths.mechanismsOfAction,
      cellStyle,
      align: "center",
      render: ({ mechanismsOfAction, isBiologic }) => (
        <MechOfActionTD
          mechanismsOfAction={mechanismsOfAction}
          noLink={noLink(isBiologic)}
          width={cellWidths.mechanismsOfAction}
        />
      ),
      customFilterAndSearch: (filterStr, rowData) => filter.tableColumn(filterStr, rowData, ["mechanismsOfAction"]),
    },
    {
      title: <ColumnHeader title={TABLE.COLUMN_TITLES.indications} handleSort={handleColumnSort} icon={<InfoIcon />} />,
      field: "indications",
      type: "string",
      width: cellWidths.indications,
      cellStyle: {
        ...cellStyle,
        position: "relative",
      },
      align: "center",
      render: ({ indicationApprovalStatus, overflow, tableData }) => (
        <OverflowWrapper
          overflow={overflow["indicationApprovalStatus"]}
          rowId={tableData.id}
          displayOverflowNote={false}
        >
          <IndicationTD
            indicationApprovalStatus={indicationApprovalStatus}
            width={cellWidths.indications}
            overflow={overflow["indicationApprovalStatus"]}
            rowId={tableData.id}
          />
        </OverflowWrapper>
      ),
      customFilterAndSearch: (filterStr, rowData) =>
        filter.tableColumn(filterStr, rowData, ["indicationApprovalStatus"]),
    },
    {
      title: <ColumnHeader title={TABLE.COLUMN_TITLES.stages} handleSort={handleColumnSort} />,
      field: "stages",
      type: "string",
      width: cellWidths.stages,
      cellStyle: {
        ...cellStyle,
        position: "relative",
      },
      align: "center",
      render: ({ indicationApprovalStatus, overflow, tableData, submissionTypes }) => {
        return (
          <OverflowWrapper overflow={overflow["indicationApprovalStatus"]} rowId={tableData.id} displayOverflowNote>
            <StageTD
              indicationApprovalStatus={indicationApprovalStatus}
              showCompletionDates={false}
              overflow={overflow["indicationApprovalStatus"]}
              rowId={tableData.id}
            />
          </OverflowWrapper>
        );
      },
    },
    {
      title: (
        <ColumnHeader
          title={TABLE.COLUMN_TITLES.submissionTypes}
          handleSort={handleColumnSort}
          icon={<TooltipHelpIcon tooltip={TEXTS.SUB_TOOLTIP} />}
        />
      ),
      field: "submissionTypes",
      type: "string",
      searchable: true,
      width: cellWidths.submissionTypes,
      cellStyle,
      align: "center",
      render: ({ submissionTypes, overflow, tableData }) => {
        return (
          <OverflowWrapper
            overflow={overflow["indicationApprovalStatus"]}
            rowId={tableData.id}
            displayOverflowNote={false}
          >
            <SubTD submissionTypes={submissionTypes} />
          </OverflowWrapper>
        );
      },
    },
    {
      title: "View",
      field: "view",
      width: cellWidths.view,
      headerStyle: {
        ...headerStyle,
        paddingRight: "1rem",
        textAlign: "right",
        paddingBottom: "1.5rem",
      },
      cellStyle,
      sorting: false,
      export: false,
      render: (rowData: any) => <ViewTD rowData={rowData} />,
    },
  ];

  return (
    <div>
      <MaterialTable
        tableRef={tableRef}
        title={TABLE.TITLE}
        columns={columns}
        data={tableData}
        icons={TableIcons}
        options={options}
        onSearchChange={(text) => handleSearchChange(text, tableRef.current.state.data)}
        localization={{
          ...localization,
          body: {
            emptyDataSourceMessage: dataContext.length > 0 ? "No Results" : <LoadingBar />,
          },
        }}
        components={{
          Toolbar: (props) => (
            <div className={classes.toolbar}>
              <MTableToolbar {...props}></MTableToolbar>
              <ToggleDetailPanels
                areAllPanelsExpanded={areAllPanelsExpanded}
                toggleAllDetailPanels={toggleAllDetailPanels}
              />
              {hasExportPermission && <ExportCSV data={tableData} />}
              <PDF
                isLoading={loading}
                handlePdfDownload={handlePdfDownload}
                isDisabled={isEmpty(dataContext) || loading}
              />
            </div>
          ),
        }}
        detailPanel={[
          ({ hasDrugPipelineReport, tags, tableData }) => ({
            icon: () => handleIcon(hasDrugPipelineReport, tags, tableData),
            openIcon: () => handleIcon(hasDrugPipelineReport, tags, tableData),
            render: handleDetailPanelRender,
          }),
        ]}
        actions={[
          (rowData: any) => ({
            icon: () => (
              <StyledTooltip arrow title={TEXTS.ICONS.magGlass} placement="bottom-end" enterDelay={500}>
                <Search className={classes.magGlass} />
              </StyledTooltip>
            ),
            onClick: (event) => openPopover(event, rowData),
          }),
        ]}
      />
      <Popup rowData={popupData} isOpen={isPopupOpen} anchorEl={anchorEl} closePopover={closePopover} />
    </div>
  );
};

export default Table;
